import { PROCESS_STATUS } from 'consts/processStatus';
import type { ProcessesResponse } from 'types';
import type { Bucket, SortDirection, SortKey } from './Admin.consts';
import { RX_UUID } from './Admin.consts';

export function formatValue(
	row: ProcessesResponse,
	field: keyof ProcessesResponse,
	t: (key: string) => string
) {
	if ('flowType' === field) {
		return t(`flow-type-${row[field]}`) ?? row[field];
	}
	if ('processStatus' === field) {
		return t(PROCESS_STATUS[row[field]]);
	}
	if ('created' === field || 'modified' === field) {
		return null === row[field] ? '' : new Date(row[field]).toLocaleString('en-GB');
	}
	return row[field];
}

// Maps the free-text search to GetProcesses query params so the server returns fresh, filtered data.
export function toServerParams(query: string): Record<string, string> {
	const trimmed = query.trim();
	if (!trimmed) return {};
	if (RX_UUID.test(trimmed)) return { agreementProcessId: trimmed };
	if (trimmed.includes('@')) return { emailInitiator: trimmed };
	const digits = trimmed.replaceAll(/\D/g, '');
	if (digits.length >= 9) return { processKennitala: digits };
	if (digits.length === 7) return { phoneInitiator: digits };
	return { fullNameInitiator: trimmed };
}

export function toBucket(status: number): Exclude<Bucket, 'all'> {
	if (status === PROCESS_STATUS.Finished) return 'done';
	if (status === PROCESS_STATUS.Expired) return 'expired';
	if (status === PROCESS_STATUS.Canceled) return 'cancelled';
	return 'active';
}

function parseDate(value: string) {
	const ts = new Date(value).getTime();
	return Number.isNaN(ts) ? 0 : ts;
}

export function isCompany(kennitala: string) {
	const day = Number.parseInt(kennitala.slice(0, 2), 10);
	return Number.isFinite(day) && day > 40;
}

export function sortRows(rows: ProcessesResponse[], key: SortKey, direction: SortDirection) {
	const dir = direction === 'asc' ? 1 : -1;
	return [...rows].sort((a, b) => {
		if (key === 'created') return (parseDate(a.created) - parseDate(b.created)) * dir;
		if (key === 'emailInitiator') return a.emailInitiator.localeCompare(b.emailInitiator, 'is') * dir;
		if (key === 'flowType') return `${a.flowType}`.localeCompare(`${b.flowType}`, 'is') * dir;
		if (key === 'name') return a.fullNameInitiator.localeCompare(b.fullNameInitiator, 'is') * dir;
		if (key === 'processStatus') return `${a.processStatus}`.localeCompare(`${b.processStatus}`, 'is') * dir;
		return (parseDate(a.modified) - parseDate(b.modified)) * dir;
	});
}

export function matchesSearch(row: ProcessesResponse, query: string, digits: string) {
	if (!query) return true;
	const haystack = [
		row.agreementProcessId,
		row.emailInitiator,
		row.fullNameInitiator,
		row.kennitala,
		row.kennitalaInitiator,
		row.phoneInitiator
	]
		.join(' ')
		.toLowerCase();
	if (haystack.includes(query)) return true;
	if (!digits) return false;
	const phone = row.phoneInitiator.replaceAll(/\D/g, '');
	return row.kennitala.includes(digits) || row.kennitalaInitiator.includes(digits) || phone.includes(digits);
}
